import { FC, useState } from 'react';
import { CompanyAWS } from '../../../@types/AWS';
import { toast } from 'react-toastify';
import Shape from '../../../@types/Shape';
import * as yup from 'yup';
import { CompanyAttributes, License } from '../../../@types/Company';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from '../../common/Modal/Modal';
import Form from '../../common/Form/Form';
import { FormProvider } from 'react-hook-form';
import { Stack } from '@mui/material';
import LoadingButton from '../../common/LoadingButton/LoadingButton';
import Button from '../../common/Button';
import axios from '../../../api/axios';
import CheckBox from '../../common/Form/FormFields/CheckBox/CheckBox';
import TextField from '../../common/Form/FormFields/TextField/TextField';
import flatten from 'flat';

interface EditAttributesModalProps {
  company: CompanyAWS;
  closeModal: () => void;
  isVisible: boolean;
}

const EditAttributesModal: FC<EditAttributesModalProps> = ({ company, closeModal, isVisible }) => {
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (company: CompanyAWS) => {
    try {
      setLoading(true);
      await axios.put('/dealer/meta-data', company);
      toast.success(`Company [${company.attributes.companyName}] attributes updated!`);
      closeModal();
    } catch (error) {
      console.log(error);
      toast.error('Server Error');
    } finally {
      setLoading(false);
    }
  }

  const onError = (errors: any) => {
    console.log(errors);
    const errorsFlattened: any = flatten(errors);
    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));
    if (Object.entries(requiredErrors).length <= 2) {
      for (const key in requiredErrors) {
        let keySplit = key.split('.');
        keySplit.pop();
        let formField = '';
        for (const fieldPart of keySplit) {
          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
        }
        toast.error(`Please check [${formField.trim()}] field for errors`);
      }
    } else {
      toast.error('Please check form for errors');
    }
  };

  const editAttributesSchema = yup.object<Shape<CompanyAWS>>({
    pk: yup.string().required(),
    sk: yup.string().required(),
    companyAdmin: yup.number().required(),
    attributes: yup.object<Shape<CompanyAttributes>>({
      companyName: yup.string().required(),
      active: yup.boolean().required(),
      international: yup.boolean().required(),
      dateCreated: yup.string().optional(),
      priorityDealer: yup.boolean().required(),
      allowLeads: yup.boolean().required(),
      logo: yup.string().optional(),
      licenses: yup.array().optional().of(
        yup.object<Shape<License>>({
          status: yup.string().required(),
          state: yup.string().required(),
          stateCode: yup.string().required(),
          type: yup.string().required().default('LSV'),
          expirationDate: yup.string().required()
        })
      )
    })
  });

  const defaultValues = { ...company };

  return (
    <>
      {isVisible &&
        <Modal title={`Edit Attributes [${company.attributes.companyName}]`} closeModal={closeModal}>
          <Form onSubmit={onSubmit} onError={onError} resolver={yupResolver(editAttributesSchema)} defaultValues={defaultValues} keepValuesOnReset={true}>
            {(methods) => (
              <FormProvider {...methods}>
                <Stack
                  spacing={2}
                  direction="column"
                  alignItems="flex-end">
                  <TextField name="attributes.companyName" label="Company Name" defaultValue={company.attributes.companyName} />
                  <CheckBox
                    name="attributes.active"
                    label="Active"
                    isTrueFalse={true}
                    defaultValue={0} />
                  <CheckBox
                    name="attributes.allowLeads"
                    label="Allow Leads"
                    isTrueFalse={true}
                    defaultValue={0} />
                  <CheckBox
                    name="attributes.international"
                    label="International Dealer"
                    isTrueFalse={true}
                    defaultValue={0} />
                    <CheckBox
                    name="attributes.priorityDealer"
                    label="Lead Priority Dealer"
                    isTrueFalse={true}
                    defaultValue={0} />
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    spacing={2}>
                    <LoadingButton loading={loading} type="submit">Submit</LoadingButton>
                    <Button onClick={closeModal} type="button">Cancel</Button>
                  </Stack>
                </Stack>
              </FormProvider>
            )}
          </Form>
        </Modal>
      }
    </>
  );
}

export default EditAttributesModal;
